import { dayjs } from '@mobi/utils/date'
import type { KambiBetOffer, KambiEvent } from '@mobi/api-types/kambi'
import type {
  KambiSportsMultiSelection,
  KambiSportsSameGameMultiSelection,
  KambiSportsSelection,
  KambiSportsSingleSelection,
} from '@mobi/betslip/types'
import { getKambiEventOutcomePricing } from '@mobi/sports-offering/helpers/api/onDemandPricing'
import { getKambiBetOffersByIds } from '@mobi/sports-offering/helpers/api/betOffers'
import type { QuickbetSelection } from '@core/Areas/Quickbet/types'
import type { KambiSportBetDetails } from '@core/Areas/ShareBet/LoadBet/types'

export const buildKambiSportsBet = async ({
  betSelectionType,
  betOfferIds,
  outcomeIds: betOutcomeIds,
  outcomesWithEarlyPayout,
}: KambiSportBetDetails): Promise<QuickbetSelection> => {
  const { betOffers: betOffersData, events } = await getKambiBetOffersByIds({ betOfferIds })
  if (betOffersData.length === 0 || events.length === 0) throw Error('Unable to build sport bet')

  const betOffers = betOffersData.flatMap(offer => {
    const outcomes = offer.outcomes.filter(outcome =>
      betOutcomeIds.some(id => (Array.isArray(id) ? id.includes(outcome.id) : id === outcome.id))
    )
    if (outcomes.length === 0) return []

    for (const earlyPayoutOutcomeId of outcomesWithEarlyPayout) {
      const outcome = outcomes.find(x => x.id === earlyPayoutOutcomeId)
      if (!outcome) continue
      outcome.outcomeTags ||= []
      outcome.outcomeTags.push('ELIGIBLE_FOR_EARLY_SETTLEMENT')
    }
    return { ...offer, outcomes }
  })

  const allValidOutcomeIds = betOffers.flatMap(({ outcomes }) => outcomes.map(({ id }) => id))

  const outcomeIds = betOutcomeIds.filter(idOrIds =>
    Array.isArray(idOrIds)
      ? idOrIds.filter(id => allValidOutcomeIds.includes(id)).length > 0
      : allValidOutcomeIds.includes(idOrIds)
  )
  if (outcomeIds.length === 0) throw Error('Unable to build sport bet')

  const selection = await createSelection({ betSelectionType, outcomeIds, events, betOffers })
  if (!selection) throw Error('Unable to build sport bet')

  return {
    bettingType: 'kambi-sports',
    selection,
    selectionDetails: { detailsType: 'kambi', events },
    isEachWayAvailable: false,
    shouldAllowPlaceInvestment: false,
  }
}

// =============
// Local Helpers
// =============

async function createSelection({
  events,
  betOffers,
  outcomeIds,
  betSelectionType,
}: {
  events: KambiEvent[]
  betOffers: KambiBetOffer[]
  outcomeIds: KambiSportBetDetails['outcomeIds']
  betSelectionType: KambiSportBetDetails['betSelectionType']
}): Promise<KambiSportsSelection | null> {
  if (betSelectionType === 'single') {
    return buildSingleSelection(betOffers[0], events)
  }

  if (betSelectionType === 'same-game-multi' && betOffers.length > 1) {
    const outcomes = outcomeIds.flat()
    const selection = await buildSameGameMultiSelection(betOffers, outcomes, events)
    return selection
  }

  if (betSelectionType === 'multi' && betOffers.length > 1) {
    const legs = await Promise.all(
      outcomeIds.map(async outcomeId => {
        // ===================
        // Same Game Multi Leg
        // ===================
        if (Array.isArray(outcomeId)) {
          const betOffersForSGM = betOffers.filter(offer =>
            offer.outcomes.some(({ id }) => outcomeId.includes(id))
          )
          if (betOffersForSGM.length === 1) {
            const betOffer = betOffersForSGM[0]
            return buildSingleSelection(betOffer, events)
          }

          const selection = await buildSameGameMultiSelection(betOffersForSGM, outcomeId, events)
          return selection
        }

        // =========
        // Singe Leg
        // =========
        const betOffer = betOffers.find(offer => offer.outcomes.some(({ id }) => id === outcomeId))
        if (!betOffer) return null
        return buildSingleSelection(betOffer, events)
      })
    )

    const validLegs = legs.filter(l => l !== null)
    if (validLegs.length === 0) return null

    if (validLegs.length === 1) {
      const betOffer = (validLegs[0] as KambiSportsSingleSelection).betOffer
      return buildSingleSelection(betOffer, events)
    }

    const multiSelection: KambiSportsMultiSelection = { betSelectionType: 'multi', legs: validLegs }
    return multiSelection
  }

  if (betOffers.length === 1) {
    return buildSingleSelection(betOffers[0], events)
  }

  return null
}

/** Single Selection Builder */
function buildSingleSelection(
  betOffer: KambiBetOffer,
  events: KambiEvent[]
): KambiSportsSingleSelection | null {
  if (!isEventOpenForBetting(betOffer, events)) return null

  const selection: KambiSportsSingleSelection = { betSelectionType: 'single', betOffer }
  return selection
}

/** SGM Selection Builder */
async function buildSameGameMultiSelection(
  betOffers: KambiBetOffer[],
  outcomeIds: number[],
  events: KambiEvent[]
) {
  if (!isEventOpenForBetting(betOffers[0], events)) return null

  const pricingResponse = await getKambiEventOutcomePricing({
    eventId: betOffers[0].eventId,
    outcomeIds,
  })
  const selection: KambiSportsSameGameMultiSelection = {
    betSelectionType: 'same-game-multi',
    betOffers,
    odds: pricingResponse.selectedOdds.decimal,
  }
  return selection
}

function isEventOpenForBetting(betOffer: KambiBetOffer, events: KambiEvent[]): boolean {
  const event = events.find(e => e.id === betOffer.eventId)
  if (!event) return false
  if (event.state !== 'NOT_STARTED' || dayjs().isAfter(betOffer.closed)) return false

  return true
}
